/**
 * Global Tableau Service
 * Manages Tableau library initialization and prevents multiple initializations
 */

declare global {
  interface Window {
    tableau?: any;
  }
}

interface TableauInitOptions {
  onReady?: () => void;
  onError?: (error: Error) => void;
}

class TableauService {
  private isInitialized = false;
  private isInitializing = false;
  private initPromise: Promise<void> | null = null;
  private readyCallbacks: (() => void)[] = [];
  private errorCallbacks: ((error: Error) => void)[] = [];

  /**
   * Initialize the Tableau library globally
   */
  async initialize(options: TableauInitOptions = {}): Promise<void> {
    // If already initialized, resolve immediately
    if (this.isInitialized && window.tableau) {
      options.onReady?.();
      return Promise.resolve();
    }

    // If currently initializing, wait for the existing promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise.then(() => {
        options.onReady?.();
      });
    }

    // Start initialization
    this.isInitializing = true;
    this.initPromise = this.performInitialization();

    // Add callbacks
    if (options.onReady) {
      this.readyCallbacks.push(options.onReady);
    }
    if (options.onError) {
      this.errorCallbacks.push(options.onError);
    }

    try {
      await this.initPromise;
      this.isInitialized = true;
      this.isInitializing = false;
      
      // Call all ready callbacks
      this.readyCallbacks.forEach(callback => callback());
      this.readyCallbacks = [];
    } catch (error) {
      this.isInitializing = false;
      const err = error instanceof Error ? error : new Error('Tableau initialization failed');
      
      // Call all error callbacks
      this.errorCallbacks.forEach(callback => callback(err));
      this.errorCallbacks = [];
      
      throw err;
    }
  }

  private async performInitialization(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if Tableau is already available
      if (window.tableau) {
        console.log('Tableau library already available');
        resolve();
        return;
      }

      // Check if script is already loaded
      const existingScript = document.querySelector('script[src*="tableau.embedding.3.latest.min.js"]');
      if (existingScript) {
        // Wait for the existing script to load
        this.waitForTableauLibrary(resolve, reject);
        return;
      }

      // Load the Tableau library
      console.log('Loading Tableau library...');
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://public.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js';
      script.async = true;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        console.log('Tableau library script loaded');
        this.waitForTableauLibrary(resolve, reject);
      };

      script.onerror = () => {
        console.error('Failed to load Tableau library script');
        reject(new Error('Failed to load Tableau library script'));
      };

      document.head.appendChild(script);
    });
  }

  private waitForTableauLibrary(resolve: () => void, reject: (error: Error) => void): void {
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait time
    const interval = 100; // Check every 100ms

    const checkTableau = () => {
      attempts++;
      
      if (window.tableau) {
        console.log('Tableau library is ready');
        resolve();
        return;
      }

      if (attempts >= maxAttempts) {
        reject(new Error('Tableau library failed to initialize within timeout'));
        return;
      }

      setTimeout(checkTableau, interval);
    };

    checkTableau();
  }

  /**
   * Check if Tableau is ready
   */
  isReady(): boolean {
    return this.isInitialized && !!window.tableau;
  }

  /**
   * Get the Tableau library instance
   */
  getTableau(): any {
    if (!this.isReady()) {
      throw new Error('Tableau library is not initialized');
    }
    return window.tableau;
  }

  /**
   * Wait for Tableau to be ready
   */
  async waitForReady(): Promise<void> {
    if (this.isReady()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for Tableau library'));
      }, 10000); // 10 second timeout

      this.initialize({
        onReady: () => {
          clearTimeout(timeout);
          resolve();
        },
        onError: (error) => {
          clearTimeout(timeout);
          reject(error);
        }
      });
    });
  }

  /**
   * Reset the service (for testing or error recovery)
   */
  reset(): void {
    this.isInitialized = false;
    this.isInitializing = false;
    this.initPromise = null;
    this.readyCallbacks = [];
    this.errorCallbacks = [];
  }
}

// Export singleton instance
export const tableauService = new TableauService();

// Export the class for testing
export { TableauService };
